import React, { useState, useEffect } from 'react';
import { 
  Building2, 
  Search, 
  Filter, 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  Eye, 
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Phone,
  Mail,
  MapPin,
  Calendar,
  User
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import api from '../../../../config/api';

export default function NGOUserManagement() {
  const [ngos, setNgos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedNGO, setSelectedNGO] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [processingId, setProcessingId] = useState(null);

  // Fetch NGOs from backend
  const fetchNGOs = async () => {
    setLoading(true);
    setError(null);
    try {
      // Use the api instance with baseURL from .env
      const response = await api.get('/ngos');
      
      console.log('NGOs fetched successfully:', response.data);
      setNgos(response.data);
    } catch (err) {
      console.error('Error fetching NGOs:', err);
      setError('Failed to load NGOs. Please try again.');
      toast.error('Failed to load NGOs');
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchNGOs();
  }, []);

  // Filter NGOs based on search term and status filter
  const filteredNGOs = ngos.filter(ngo => {
    const matchesSearch = 
      ngo.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ngo.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (ngo.phoneNumber && ngo.phoneNumber.includes(searchTerm));
    
    const matchesStatus = 
      statusFilter === 'all' || 
      ngo.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentNGOs = filteredNGOs.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredNGOs.length / itemsPerPage);

  // Handle page change
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // View NGO details
  const viewNGODetails = (ngo) => {
    setSelectedNGO(ngo);
    setShowDetailsModal(true);
  };

  // Close details modal
  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedNGO(null);
  };

  // Update NGO status
  const updateNGOStatus = async (ngoId, newStatus) => {
    setProcessingId(ngoId);
    try {
      // Use the api instance with baseURL from .env
      const response = await api.put(`/ngos/${ngoId}/status`, { status: newStatus });
      
      // Update local state
      setNgos(ngos.map(ngo => 
        ngo._id === ngoId ? { ...ngo, status: newStatus } : ngo
      ));
      
      // Update selected NGO if it's the one being modified
      if (selectedNGO && selectedNGO._id === ngoId) {
        setSelectedNGO({ ...selectedNGO, status: newStatus });
      }
      
      toast.success(`NGO status updated to ${newStatus}`);
    } catch (err) {
      console.error('Error updating NGO status:', err);
      toast.error('Failed to update NGO status');
    } finally {
      setProcessingId(null);
    }
  };

  // Status badge component
  const StatusBadge = ({ status }) => {
    let bgColor, textColor, icon;
    
    switch(status) {
      case 'approved':
        bgColor = 'bg-green-100';
        textColor = 'text-green-800';
        icon = <CheckCircle size={14} className="mr-1" />;
        break;
      case 'rejected':
        bgColor = 'bg-red-100';
        textColor = 'text-red-800';
        icon = <XCircle size={14} className="mr-1" />;
        break;
      default: // pending
        bgColor = 'bg-yellow-100';
        textColor = 'text-yellow-800';
        icon = <AlertCircle size={14} className="mr-1" />;
    }
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
        {icon}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center">
          <Building2 className="mr-2 text-red-600" />
          NGO Management
        </h1>
        <p className="text-gray-600">Manage registered NGOs and their approval status</p>
      </div>

      {/* Filters and Search */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="relative w-full md:w-64">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search size={18} className="text-gray-400" />
          </div>
          <input
            type="text"
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full focus:ring-2 focus:ring-red-500 focus:border-red-500"
            placeholder="Search NGOs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="flex items-center space-x-4">
          <div className="flex items-center">
            <Filter size={18} className="text-gray-500 mr-2" />
            <select
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          <button
            onClick={fetchNGOs}
            className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            disabled={loading}
          >
            <RefreshCw size={18} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
          <AlertCircle className="text-red-500 mr-3 mt-0.5 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* NGO Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  NGO Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Location
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Registration Date
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                    <div className="flex justify-center items-center">
                      <RefreshCw size={20} className="animate-spin mr-2" />
                      Loading NGOs...
                    </div>
                  </td>
                </tr>
              ) : currentNGOs.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                    No NGOs found
                  </td>
                </tr>
              ) : (
                currentNGOs.map((ngo) => (
                  <tr key={ngo._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-red-100 rounded-full flex items-center justify-center">
                          {ngo.logo ? (
                            <img src={ngo.logo} alt={ngo.name} className="h-10 w-10 rounded-full object-cover" />
                          ) : (
                            <Building2 size={20} className="text-red-600" />
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{ngo.name}</div>
                          <div className="text-sm text-gray-500">ID: {ngo._id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{ngo.email}</div>
                      <div className="text-sm text-gray-500">{ngo.phoneNumber}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{ngo.city}</div>
                      <div className="text-sm text-gray-500">{ngo.state}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={ngo.status} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(ngo.registrationDate).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => viewNGODetails(ngo)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        <Eye size={18} />
                      </button>
                      {/* Show all status options regardless of current status */}
                      {ngo.status !== 'approved' && (
                        <button
                          onClick={() => updateNGOStatus(ngo._id, 'approved')}
                          className="text-green-600 hover:text-green-900 mr-3"
                          disabled={processingId === ngo._id}
                          title="Approve NGO"
                        >
                          <CheckCircle size={18} className={processingId === ngo._id ? 'animate-pulse' : ''} />
                        </button>
                      )}
                      
                      {ngo.status !== 'rejected' && (
                        <button
                          onClick={() => updateNGOStatus(ngo._id, 'rejected')}
                          className="text-red-600 hover:text-red-900 mr-3"
                          disabled={processingId === ngo._id}
                          title="Reject NGO"
                        >
                          <XCircle size={18} className={processingId === ngo._id ? 'animate-pulse' : ''} />
                        </button>
                      )}
                      
                      {ngo.status !== 'pending' && (
                        <button
                          onClick={() => updateNGOStatus(ngo._id, 'pending')}
                          className="text-yellow-600 hover:text-yellow-900"
                          disabled={processingId === ngo._id}
                          title="Mark as Pending"
                        >
                          <AlertCircle size={18} className={processingId === ngo._id ? 'animate-pulse' : ''} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {!loading && filteredNGOs.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{' '}
            <span className="font-medium">
              {indexOfLastItem > filteredNGOs.length ? filteredNGOs.length : indexOfLastItem}
            </span>{' '}
            of <span className="font-medium">{filteredNGOs.length}</span> NGOs
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-3 py-1 rounded-md ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            >
              <ChevronLeft size={16} />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
              <button
                key={number}
                onClick={() => paginate(number)}
                className={`px-3 py-1 rounded-md ${currentPage === number ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              >
                {number}
              </button>
            ))}
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-3 py-1 rounded-md ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* NGO Details Modal */}
      {showDetailsModal && selectedNGO && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                      <Building2 className="mr-2 text-red-600" />
                      {selectedNGO.name}
                    </h3>
                    <div className="mt-4">
                      <div className="flex justify-between items-center mb-4">
                        <StatusBadge status={selectedNGO.status} />
                        <span className="text-sm text-gray-500">
                          Registered on: {new Date(selectedNGO.registrationDate).toLocaleDateString()}
                        </span>
                      </div>

                      <div className="bg-gray-50 p-4 rounded-lg mb-4">
                        <h4 className="font-medium text-gray-700 mb-2">Contact Information</h4>
                        <div className="space-y-2">
                          <div className="flex items-start">
                            <Mail className="text-gray-500 mt-0.5 mr-2 flex-shrink-0" size={16} />
                            <span className="text-gray-800">{selectedNGO.email}</span>
                          </div>
                          <div className="flex items-start">
                            <Phone className="text-gray-500 mt-0.5 mr-2 flex-shrink-0" size={16} />
                            <span className="text-gray-800">{selectedNGO.phoneNumber}</span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gray-50 p-4 rounded-lg mb-4">
                        <h4 className="font-medium text-gray-700 mb-2">Personal Contact</h4>
                        <div className="space-y-2">
                          <div className="flex items-start">
                            <User className="text-gray-500 mt-0.5 mr-2 flex-shrink-0" size={16} />
                            <span className="text-gray-800">{selectedNGO.personalName}</span>
                          </div>
                          <div className="flex items-start">
                            <Phone className="text-gray-500 mt-0.5 mr-2 flex-shrink-0" size={16} />
                            <span className="text-gray-800">{selectedNGO.personalPhone}</span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gray-50 p-4 rounded-lg mb-4">
                        <h4 className="font-medium text-gray-700 mb-2">Location</h4>
                        <div className="space-y-2">
                          <div className="flex items-start">
                            <MapPin className="text-gray-500 mt-0.5 mr-2 flex-shrink-0" size={16} />
                            <span className="text-gray-800">
                              {selectedNGO.city}, {selectedNGO.state}, {selectedNGO.pincode}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-medium text-gray-700 mb-2">Additional Information</h4>
                        <div className="space-y-2">
                          <div className="flex items-start">
                            <Calendar className="text-gray-500 mt-0.5 mr-2 flex-shrink-0" size={16} />
                            <span className="text-gray-800">
                              Started operations on: {new Date(selectedNGO.startDate).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                {selectedNGO.status === 'pending' && (
                  <>
                    <button
                      type="button"
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm"
                      onClick={() => {
                        updateNGOStatus(selectedNGO._id, 'approved');
                        closeDetailsModal();
                      }}
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                      onClick={() => {
                        updateNGOStatus(selectedNGO._id, 'rejected');
                        closeDetailsModal();
                      }}
                    >
                      Reject
                    </button>
                  </>
                )}
                {selectedNGO.status === 'approved' && (
                  <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={() => {
                      updateNGOStatus(selectedNGO._id, 'rejected');
                      closeDetailsModal();
                    }}
                  >
                    Reject
                  </button>
                )}
                {selectedNGO.status === 'rejected' && (
                  <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={() => {
                      updateNGOStatus(selectedNGO._id, 'approved');
                      closeDetailsModal();
                    }}
                  >
                    Approve
                  </button>
                )}
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={closeDetailsModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}